/**
 * VersonaLabModule — the GROUP versona observatory (F4).
 *
 * Where the Alt+Click inspector is a microscope on ONE versona, the Lab is the
 * field station for a whole town: a live roster of the nearest versonas (needs,
 * mood, dominant feeling, intent, encounters), a rolling interaction feed
 * (encounters, vents, loans, trials, market days, chats — diffed live from the
 * event tables), one-click "make them feel/behave" controls, and scripted GROUP
 * scenarios (hunger crisis, feud, friendship, cheer, direct command) that run
 * the SAME engine as `?probe=versonalab` and `verify:versonalab` and end in a
 * copyable behavior report.
 *
 * Anyone can OBSERVE. Every mutation goes through the admin-gated debug
 * reducers — without the admin cap the feel/scenario controls are disabled and
 * the Lab is a pure observatory. Heavy tables are subscribed lazily on first
 * open; the two append-only giants (npc_event, chronicle) are subscribed
 * WHERE-scoped per settlement.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { capsIn } from '../lib/world_caps.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
import {
  labBaseSubs, labScopedSubs, labVersonaSubs, pickCast, runLab, sampleVersona, labelOf, tbl,
  InteractionWatch, renderLabReportMd, labRunToJson, LAB_SCENARIO_NAMES,
  labReducers, type LabCast, type LabRun,
} from '../lib/versona_lab.js';

const ROSTER_N = 12;

interface SayReducer { versonaSay(a: { versonaId: bigint; text: string }): Promise<void> }
interface EconRowLite { settlementId: bigint; centerX: number; centerZ: number }
interface AgentRowLite { versonaId: bigint; settlementId: bigint }

export class VersonaLabModule implements IModule {
  readonly name = 'VersonaLab';

  private win: UiWindow | null = null;
  private roster: HTMLDivElement | null = null;
  private feed: HTMLDivElement | null = null;
  private status: HTMLDivElement | null = null;
  private controls: HTMLDivElement | null = null;
  private selected: bigint[] = [];               // ordered — [0]=A, [1]=B
  private timer: number | null = null;
  private subscribed = false;
  private scopedSids = new Set<string>();
  private watch: InteractionWatch | null = null;
  private watchSid: bigint | null = null;
  private running = false;
  private lastRun: LabRun | null = null;

  constructor(private readonly env: EnvironmentModule) {}

  init(_app: App): void {
    void _app;
    window.addEventListener('keydown', (e) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || (t instanceof Element && t.closest('.cm-editor')))) return;
      if (e.code !== 'F4' || e.ctrlKey || e.metaKey || e.altKey) return;
      e.preventDefault();
      this.toggle();
    });
  }

  private isAdmin(): boolean {
    return atlas.currentWorldId !== null
      && atlas.currentWorldId === atlas.commonsWorldId
      && capsIn(atlas.currentWorldId).admin;
  }

  private subHandles: Array<{ unsubscribe(): void }> = [];
  private ensureSubs(): void {
    if (!this.subscribed) {
      this.subscribed = true;
      // One query per set: a 21-query set silently never applied in the browser SDK (Versona Universe probe, 2026-08-21).
      try {
        for (const q of labBaseSubs()) {
          const h = atlas.conn?.subscriptionBuilder().subscribe([q]) as { unsubscribe(): void } | undefined;
          if (h) this.subHandles.push(h);
        }
      } catch { /* pre-publish server */ }
    }
    const sid = this.nearestSettlement();
    if (sid !== null && !this.scopedSids.has(sid.toString())) {
      this.scopedSids.add(sid.toString());
      try {
        for (const q of labScopedSubs(sid)) {
          const h = atlas.conn?.subscriptionBuilder().subscribe([q]) as { unsubscribe(): void } | undefined;
          if (h) this.subHandles.push(h);
        }
      } catch { /* ok */ }
    }
  }

  /** (bug sweep) the observatory's heavy whole-table subs (versona_memory,
   *  emotion, chat, encounter…) used to live for the whole session after ONE
   *  F4 — release them all when the window closes; reopening re-subscribes. */
  private releaseSubs(): void {
    for (const h of this.subHandles) { try { h.unsubscribe(); } catch { /* gone */ } }
    this.subHandles = [];
    this.subscribed = false;
    this.scopedSids.clear();
    for (const h of this.versonaSubs.values()) { try { h.unsubscribe(); } catch { /* gone */ } }
    this.versonaSubs.clear();
  }

  /** The selected versonas' memory rows (per-id subscriptions; dropped when deselected). */
  private versonaSubs = new Map<string, { unsubscribe(): void }>();
  private syncVersonaSubs(): void {
    const want = new Set(this.selected.map((id) => id.toString()));
    for (const [k, h] of this.versonaSubs) if (!want.has(k)) { try { h.unsubscribe(); } catch { /* gone */ } this.versonaSubs.delete(k); }
    for (const id of this.selected) {
      const k = id.toString();
      if (this.versonaSubs.has(k)) continue;
      try {
        const h = atlas.conn?.subscriptionBuilder().subscribe(labVersonaSubs(id)) as { unsubscribe(): void } | undefined;
        if (h) this.versonaSubs.set(k, h);
      } catch { /* pre-publish server */ }
    }
  }

  private nearestSettlement(): bigint | null {
    let best: bigint | null = null, bestD = Infinity;
    for (const e of tbl<EconRowLite>(atlas.conn, 'settlementEcon')) {
      const d = Math.hypot(e.centerX - atlas.localX, e.centerZ - atlas.localZ);
      if (d < bestD) { bestD = d; best = e.settlementId; }
    }
    return best;
  }

  private nearbyIds(): bigint[] {
    return this.env.versonaPositions()
      .map((p) => ({ id: p.id, d: Math.hypot(p.x - atlas.localX, p.z - atlas.localZ) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, ROSTER_N)
      .map((p) => BigInt(p.id));
  }

  // ─── Window ────────────────────────────────────────────────────────────────

  private toggle(): void {
    if (this.win?.isOpen()) { this.win.setOpen(false); this.releaseSubs(); return; }
    this.open();
  }

  private open(): void {
    this.ensureSubs();
    if (!this.win) {
      this.win = makeWindow({
        id: 'versona-lab', title: 'Versona Lab — group observatory', icon: 'people',
        width: 780, height: 720, minWidth: 560, minHeight: 420, x: 60, y: 40,
        onClose: () => this.releaseSubs(), // the × never released the 20 whole-table live subscriptions (2026-09-12)
      });
      const p = document.createElement('div');
      p.style.cssText = 'display:flex;flex-direction:column;height:100%;color:var(--ax-text);font:12px/1.5 var(--ax-font);box-sizing:border-box';
      this.status = document.createElement('div');
      this.status.style.cssText = 'padding:5px 8px;border-bottom:1px solid var(--ax-line);opacity:.9';
      p.appendChild(this.status);
      this.roster = document.createElement('div');
      this.roster.style.cssText = 'max-height:42%;overflow:auto;padding:4px 8px;border-bottom:1px solid var(--ax-line)';
      p.appendChild(this.roster);
      this.controls = document.createElement('div');
      this.controls.style.cssText = 'padding:5px 8px;border-bottom:1px solid var(--ax-line);display:flex;flex-direction:column;gap:4px';
      p.appendChild(this.controls);
      this.feed = document.createElement('div');
      this.feed.style.cssText = 'flex:1;overflow:auto;padding:5px 8px;font-family:var(--ax-mono);font-size:11px;white-space:pre-wrap';
      p.appendChild(this.feed);
      this.win.body.appendChild(p);
      this.buildControls();
    }
    this.win.setOpen(true);
    if (this.timer === null) {
      this.timer = window.setInterval(() => {
        if (this.win?.isOpen()) this.refresh();
        else if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
      }, 2000);
    }
    this.refresh();
  }

  private refresh(): void {
    this.ensureSubs();
    const sid = this.nearestSettlement();
    if (this.status) {
      const admin = this.isAdmin();
      this.status.textContent = `settlement ${sid ?? '…'} · ${admin ? 'ADMIN — full control' : 'observe-only (no admin cap)'} · selected: ${this.selected.map((id) => labelOf(atlas.conn, id)).join(' + ') || 'none (click rows — first = A, second = B)'}${this.running ? ' · SCENARIO RUNNING' : ''}`;
    }
    this.renderRoster();
    if (sid !== null) {
      if (this.watchSid !== sid) { this.watchSid = sid; this.watch = new InteractionWatch(sid); this.watch.drain(atlas.conn, Date.now()); }
      else if (this.watch) {
        for (const i of this.watch.drain(atlas.conn, Date.now())) this.feedLine(i.line);
      }
    }
  }

  private renderRoster(): void {
    if (!this.roster) return;
    const b = this.roster;
    b.textContent = '';
    const now = Date.now();
    const head = document.createElement('div');
    head.style.cssText = 'display:grid;grid-template-columns:1.6fr 1fr .5fr .5fr .5fr .5fr .6fr 1.4fr 1.2fr;gap:4px;opacity:.65;font-size:10px';
    for (const h of ['versona', 'doing', 'hun', 'rst', 'soc', 'mood', 'ⓒ', 'feeling', 'encounter']) {
      const s = document.createElement('span'); s.textContent = h; head.appendChild(s);
    }
    b.appendChild(head);
    for (const id of this.nearbyIds()) {
      const s = sampleVersona(atlas.conn, id, now);
      if (!s) continue;
      const row = document.createElement('div');
      const selIdx = this.selected.indexOf(id);
      row.style.cssText = 'display:grid;grid-template-columns:1.6fr 1fr .5fr .5fr .5fr .5fr .6fr 1.4fr 1.2fr;gap:4px;cursor:pointer;padding:1px 2px;border-radius:var(--ax-radius);font-family:var(--ax-mono);font-size:11px'
        + (selIdx === 0 ? ';background:color-mix(in srgb, var(--ax-good) 22%, var(--ax-bg2))' : selIdx === 1 ? ';background:var(--ax-bg3)' : '');
      const top = s.emotions[0];
      const cells = [
        `${selIdx === 0 ? 'A· ' : selIdx === 1 ? 'B· ' : ''}${labelOf(atlas.conn, id)}`,
        s.activity, String(s.hunger), String(s.needRest), String(s.needSocial), String(s.mood), String(s.wallet),
        top ? `${top.etype} ${top.eff}` : '—',
        s.encounter ?? '—',
      ];
      for (const c of cells) { const sp = document.createElement('span'); sp.textContent = c; sp.style.overflow = 'hidden'; sp.style.textOverflow = 'ellipsis'; sp.style.whiteSpace = 'nowrap'; row.appendChild(sp); }
      row.title = `${s.goal ? `goal ${s.goal} · ` : ''}${s.emotions.map((e) => `${e.etype} ${e.eff}${e.target !== 0n ? `@#${e.target}` : ''}`).join(', ') || 'no active feelings'} · ${s.grudges} grudge(s) · pantry ${s.pantryDays ?? '?'}d (Alt+Click in-world for the full inspector)`;
      row.onclick = () => {
        const i = this.selected.indexOf(id);
        if (i >= 0) this.selected.splice(i, 1);
        else { this.selected.push(id); if (this.selected.length > 2) this.selected.shift(); }
        this.syncVersonaSubs();
        this.refresh();
      };
      b.appendChild(row);
    }
  }

  // ─── Controls ──────────────────────────────────────────────────────────────

  private btn(label: string, tip: string, onClick: () => void): HTMLButtonElement {
    const el = document.createElement('button');
    el.textContent = label;
    el.title = tip;
    el.style.cssText = 'padding:2px 7px;background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);font:11px var(--ax-font);cursor:pointer';
    el.onclick = onClick;
    return el;
  }

  private feedLine(text: string): void {
    if (!this.feed) return;
    const atBottom = this.feed.scrollTop + this.feed.clientHeight >= this.feed.scrollHeight - 24;
    const d = document.createElement('div');
    d.textContent = `${new Date().toISOString().slice(11, 19)} ${text}`;
    this.feed.appendChild(d);
    while (this.feed.childNodes.length > 250) this.feed.removeChild(this.feed.firstChild!);
    if (atBottom) this.feed.scrollTop = this.feed.scrollHeight;
  }

  private note(text: string, err = false): void {
    this.feedLine(err ? `⚠ ${text}` : text);
  }

  private call(p: Promise<void> | undefined, done: string): void {
    if (!p) { this.note('no connection', true); return; }
    void p.then(() => this.note(done)).catch((e: Error) => this.note(e.message, true));
  }

  private a(): bigint | null { return this.selected[0] ?? null; }
  private b(): bigint | null { return this.selected[1] ?? null; }

  private buildControls(): void {
    if (!this.controls) return;
    const c = this.controls;
    const admin = this.isAdmin();
    const r = labReducers(atlas.conn);

    const feelRow = document.createElement('div');
    feelRow.style.cssText = 'display:flex;gap:4px;flex-wrap:wrap;align-items:center';
    const lab1 = document.createElement('span'); lab1.textContent = 'feel:'; lab1.style.opacity = '.7';
    feelRow.appendChild(lab1);
    const needA = (fn: (a: bigint) => void) => () => { const x = this.a(); if (x === null) { this.note('select a versona first (click a row)', true); return; } fn(x); };
    const needAB = (fn: (a: bigint, b: bigint) => void) => () => { const x = this.a(), y = this.b(); if (x === null || y === null) { this.note('select TWO versonas (A then B)', true); return; } fn(x, y); };
    feelRow.appendChild(this.btn('cheer A', 'joy 85', needA((x) => this.call(r?.debugElicitEmotion({ versonaId: x, etype: 'joy', intensity: 85, targetId: 0n, causeText: 'a lab blessing' }), `cheered ${labelOf(atlas.conn, x)}`))));
    feelRow.appendChild(this.btn('frighten A', 'fear 70', needA((x) => this.call(r?.debugElicitEmotion({ versonaId: x, etype: 'fear', intensity: 70, targetId: 0n, causeText: 'something moved in the dark' }), `frightened ${labelOf(atlas.conn, x)}`))));
    feelRow.appendChild(this.btn('sadden A', 'distress 65', needA((x) => this.call(r?.debugElicitEmotion({ versonaId: x, etype: 'distress', intensity: 65, targetId: 0n, causeText: 'a heavy heart today' }), `saddened ${labelOf(atlas.conn, x)}`))));
    feelRow.appendChild(this.btn('enrage A@B', 'anger 80 at B + grudge 85', needAB((x, y) => {
      this.call(r?.debugElicitEmotion({ versonaId: x, etype: 'anger', intensity: 80, targetId: y, causeText: 'an unforgivable slight' }), `enraged ${labelOf(atlas.conn, x)} at ${labelOf(atlas.conn, y)}`);
      this.call(r?.debugSetGrudge({ versonaId: x, targetId: y, intensity: 85, causeText: 'an unforgivable slight' }), 'grudge set');
    })));
    feelRow.appendChild(this.btn('befriend A+B', 'pair familiarity 85 (stages encounters/gossip/unions)', needAB((x, y) => this.call(r?.debugSetPair({ aId: x, bId: y, familiarity: 85 }), `befriended ${labelOf(atlas.conn, x)} + ${labelOf(atlas.conn, y)}`))));
    feelRow.appendChild(this.btn('calm A', 'clear-affect (wipes episodes + resets mood)', needA((x) => this.call(r?.debugClearAffect({ versonaId: x }), `calmed ${labelOf(atlas.conn, x)}`))));
    c.appendChild(feelRow);

    const doRow = document.createElement('div');
    doRow.style.cssText = 'display:flex;gap:4px;flex-wrap:wrap;align-items:center';
    const lab2 = document.createElement('span'); lab2.textContent = 'do:'; lab2.style.opacity = '.7';
    doRow.appendChild(lab2);
    doRow.appendChild(this.btn('starve A', 'hunger 96 + broke → crisis path (loan/welfare/anger)', needA((x) => {
      this.call(r?.debugSetAgentStat({ versonaId: x, field: 'hunger', value: 96n }), `starving ${labelOf(atlas.conn, x)}`);
      this.call(r?.debugSetAgentStat({ versonaId: x, field: 'wallet', value: 0n }), 'and broke');
    })));
    doRow.appendChild(this.btn('exhaust A', 'needRest 100 → downs tools', needA((x) => this.call(r?.debugSetAgentStat({ versonaId: x, field: 'needRest', value: 100n }), `exhausted ${labelOf(atlas.conn, x)}`))));
    doRow.appendChild(this.btn('enrich A', 'wallet +100ⓒ', needA((x) => {
      const cur = tbl<{ versonaId: bigint; wallet: bigint }>(atlas.conn, 'npcAgent').find((q) => q.versonaId === x)?.wallet ?? 0n;
      this.call(r?.debugSetAgentStat({ versonaId: x, field: 'wallet', value: cur + 100n }), `enriched ${labelOf(atlas.conn, x)}`);
    })));
    doRow.appendChild(this.btn('come here A', 'goal = my position (60 s)', needA((x) => this.call(r?.setVersonaGoal({ versonaId: x, x: atlas.localX, z: atlas.localZ, state: 'working', ttlSeconds: 60 }), `summoned ${labelOf(atlas.conn, x)}`))));
    doRow.appendChild(this.btn('meet A+B', 'send both to the midpoint between them', needAB((x, y) => {
      const now = Date.now();
      const sa = sampleVersona(atlas.conn, x, now), sb = sampleVersona(atlas.conn, y, now);
      if (!sa || !sb) { this.note('positions unknown', true); return; }
      const mx = (sa.x + sb.x) / 2, mz = (sa.z + sb.z) / 2;
      this.call(r?.setVersonaGoal({ versonaId: x, x: mx, z: mz, state: 'working', ttlSeconds: 90 }), `${labelOf(atlas.conn, x)} heading to the meeting spot`);
      this.call(r?.setVersonaGoal({ versonaId: y, x: mx + 2, z: mz, state: 'working', ttlSeconds: 90 }), `${labelOf(atlas.conn, y)} heading to the meeting spot`);
    })));
    doRow.appendChild(this.btn('say as A…', 'speak as this versona', needA((x) => {
      const text = prompt('Say as this versona:');
      if (text?.trim()) this.call((atlas.conn?.reducers as unknown as SayReducer | undefined)?.versonaSay({ versonaId: x, text: text.trim() }), 'said');
    })));
    c.appendChild(doRow);

    const scRow = document.createElement('div');
    scRow.style.cssText = 'display:flex;gap:4px;flex-wrap:wrap;align-items:center';
    const lab3 = document.createElement('span'); lab3.textContent = 'scenario:'; lab3.style.opacity = '.7';
    scRow.appendChild(lab3);
    for (const name of LAB_SCENARIO_NAMES) {
      scRow.appendChild(this.btn(name, `run the ${name} scenario on the nearby cast (~60 s)`, () => void this.runScenarios([name])));
    }
    scRow.appendChild(this.btn('FULL RUN', 'all scenarios in order (~6 min) → report', () => void this.runScenarios(undefined)));
    scRow.appendChild(this.btn('copy report', 'copy the last run as markdown (JSON to console)', () => {
      if (!this.lastRun) { this.note('no completed run yet', true); return; }
      const md = renderLabReportMd(this.lastRun);
      void navigator.clipboard?.writeText(md).then(() => this.note('report copied (markdown)')).catch(() => this.note('clipboard unavailable — logged to console', true));
      // eslint-disable-next-line no-console
      console.log('[versona-lab] report JSON:', labRunToJson(this.lastRun));
    }));
    c.appendChild(scRow);

    if (!admin) {
      const warn = document.createElement('div');
      warn.style.cssText = 'opacity:.65;font-size:11px';
      warn.textContent = 'observe-only: feel/do/scenario injection needs the Commons admin capability (the server refuses without it) — baseline still runs.';
      c.appendChild(warn);
    }
  }

  private async runScenarios(names: string[] | undefined): Promise<void> {
    if (this.running) { this.note('a scenario is already running', true); return; }
    const sid = this.nearestSettlement();
    if (sid === null) { this.note('no settlement data yet', true); return; }
    // Selection (≥2) becomes the head of the cast; the rest is auto-picked.
    let cast: LabCast | null = pickCast(atlas.conn, Date.now(), sid, 6);
    if (cast && this.selected.length >= 1) {
      const rest = cast.ids.filter((id) => !this.selected.includes(id));
      cast = { ...cast, ids: [...this.selected, ...rest].slice(0, 6) };
    }
    if (!cast) { this.note('not enough adult versonas here for a cast', true); return; }
    this.running = true;
    this.note(`— scenario run starting (${names?.join(', ') ?? 'FULL'}) —`);
    try {
      this.lastRun = await runLab(atlas.conn, cast, {
        windowMs: 60_000, sampleMs: 3_000,
        canInject: this.isAdmin(),
        scenarios: names,
        log: (l) => this.feedLine(l),
        sleep: (ms) => new Promise((res) => setTimeout(res, ms)),
        onInteraction: () => undefined, // engine logs via `log`; the panel feed also drains its own watch
      });
      const ck = this.lastRun.checks;
      this.note(`— run complete: ${ck.passed} ✓ / ${ck.failed} flags (${ck.hardFailed} hard) — "copy report" for the full markdown —`);
    } catch (e) {
      this.note(`run failed: ${(e as Error).message}`, true);
    } finally {
      this.running = false;
    }
  }

  dispose(): void {
    this.releaseSubs();
    if (this.timer !== null) window.clearInterval(this.timer);
    this.win?.root.remove();
  }
}
